import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  primaryKey,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import { lessons, organisations, practiceTodos, students, users } from "./schema.js";
import { studentGoals } from "./studentGoals.js";

const id = () =>
  varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`);

const organisationId = () =>
  varchar("organisation_id")
    .notNull()
    .references(() => organisations.id, { onDelete: "restrict" });

const createdAt = () => timestamp("created_at", { withTimezone: true }).notNull().defaultNow();

/**
 * The existing `resources` table, extended into the reusable teaching-resource
 * domain. It deliberately keeps the legacy column names (`name`, `file_name`,
 * `object_key`) so imported records remain readable without a destructive
 * migration or a duplicate table.
 */
export const resourceRecords = pgTable(
  "resources",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id").references(() => students.id, { onDelete: "cascade" }),
    // Retained legacy columns and their existing database relationship.
    lessonId: varchar("lesson_id").references(() => lessons.id, { onDelete: "set null" }),
    resourceType: text("resource_type"),
    notionPageId: text("notion_page_id"),
    title: text("name").notNull(),
    description: text("description"),
    /** Null only for an untouched legacy row. New rows are file, link or hub_asset. */
    kind: text("kind"),
    objectKey: text("object_key"),
    originalFilename: text("file_name"),
    externalUrl: text("external_url"),
    /** Hub-native teaching asset provider, e.g. groove or fretboard_pattern. */
    hubProvider: text("hub_provider"),
    /** Provider-owned id; meaningful only when hubProvider is present. */
    hubAssetId: text("hub_asset_id"),
    contentType: text("content_type"),
    sizeBytes: integer("size_bytes"),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    // Fail closed for untouched legacy rows. The resource API explicitly sets
    // this field for every new file/link, so user-facing defaults remain unchanged.
    familyVisible: boolean("family_visible").notNull().default(false),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("resources_student_idx").on(t.studentId),
    index("resources_student_active_idx").on(
      t.organisationId,
      t.studentId,
      t.archivedAt,
      t.createdAt,
    ),
    uniqueIndex("resources_hub_asset_unique_idx")
      .on(t.organisationId, t.hubProvider, t.hubAssetId)
      .where(sql`${t.hubProvider} is not null and ${t.hubAssetId} is not null`),
  ],
);

export const MAX_REPERTOIRE_FILE_BATCH = 20;
export interface RepertoireFileBatchResult {
  libraryPieceId: string;
  items: { source: "resource" | "school-resource" | "lesson" | "school"; id: string; resourceId: string; copied: boolean }[];
}

/** Successful bulk saves are durable so response loss cannot duplicate copies. */
export const repertoireFileBatches = pgTable("repertoire_file_batches", {
  organisationId: organisationId(),
  createdByUserId: varchar("created_by_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  requestId: varchar("request_id").notNull(),
  inputHash: text("input_hash").notNull(),
  result: jsonb("result").$type<RepertoireFileBatchResult & { sourceHashes: string[] }>().notNull(),
  createdAt: createdAt(),
}, (t) => [primaryKey({ name: "repertoire_file_batches_pkey", columns: [t.organisationId, t.createdByUserId, t.requestId] })]);

/** One reusable resource may support many practice tasks. */
export const resourcePracticeTodoLinks = pgTable(
  "resource_practice_todo_links",
  {
    id: id(),
    organisationId: organisationId(),
    resourceId: varchar("resource_id")
      .notNull()
      .references(() => resourceRecords.id, { onDelete: "cascade" }),
    practiceTodoId: varchar("practice_todo_id")
      .notNull()
      .references(() => practiceTodos.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("resource_practice_todo_pair_idx").on(t.resourceId, t.practiceTodoId),
    index("resource_practice_todo_target_idx").on(t.organisationId, t.practiceTodoId),
  ],
);

/** One reusable resource may support many goals. */
export const resourceGoalLinks = pgTable(
  "resource_goal_links",
  {
    id: id(),
    organisationId: organisationId(),
    resourceId: varchar("resource_id")
      .notNull()
      .references(() => resourceRecords.id, { onDelete: "cascade" }),
    goalId: varchar("goal_id")
      .notNull()
      .references(() => studentGoals.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("resource_goal_pair_idx").on(t.resourceId, t.goalId),
    index("resource_goal_target_idx").on(t.organisationId, t.goalId),
  ],
);

/** Kept aligned with the existing practice-attachment ceiling. */
export const MAX_RESOURCE_BYTES = 50 * 1024 * 1024;

/**
 * Deliberate allowlist. SVG/HTML/script/archive formats are absent because a
 * browser may execute them rather than merely display them.
 */
export const ALLOWED_RESOURCE_TYPES: readonly string[] = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
  "audio/mpeg",
  "audio/mp4",
  "audio/x-m4a",
  "audio/aac",
  "audio/wav",
  "audio/x-wav",
  "audio/ogg",
  "audio/webm",
  "audio/flac",
  "video/mp4",
  "video/webm",
  "video/quicktime",
];

export function describeResourceFileProblem(fileType: string, fileSize: number): string | null {
  if (!ALLOWED_RESOURCE_TYPES.includes(fileType.toLowerCase())) {
    return "That kind of file cannot be shared — use a PDF, image, audio file or supported video.";
  }
  if (fileSize <= 0) return "That file is empty.";
  if (fileSize > MAX_RESOURCE_BYTES) {
    return `That file is larger than ${MAX_RESOURCE_BYTES / (1024 * 1024)} MB.`;
  }
  return null;
}

/**
 * MIME is supplied by the uploading browser, so the server also checks a small
 * set of stable file signatures before bytes are put in R2. This is not a media
 * decoder; it is a guard against an HTML/script/archive payload simply claiming
 * to be a PDF or image.
 */
export function resourceBytesMatchType(fileType: string, body: Buffer): boolean {
  const type = fileType.toLowerCase();
  const ascii = (start: number, end: number) => body.subarray(start, end).toString("ascii");
  const starts = (...bytes: number[]) =>
    body.length >= bytes.length && bytes.every((value, index) => body[index] === value);
  const ftyp = body.length >= 12 && ascii(4, 8) === "ftyp";

  switch (type) {
    case "application/pdf":
      return ascii(0, 5) === "%PDF-";
    case "image/jpeg":
      return starts(0xff, 0xd8, 0xff);
    case "image/png":
      return starts(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a);
    case "image/gif":
      return ascii(0, 6) === "GIF87a" || ascii(0, 6) === "GIF89a";
    case "image/webp":
      return body.length >= 12 && ascii(0, 4) === "RIFF" && ascii(8, 12) === "WEBP";
    case "image/heic":
    case "image/heif": {
      if (!ftyp) return false;
      const brand = ascii(8, 12).toLowerCase();
      return ["heic", "heix", "hevc", "hevx", "heim", "heis", "mif1", "msf1"].includes(brand);
    }
    case "audio/mpeg":
      return ascii(0, 3) === "ID3" || (body.length >= 2 && body[0] === 0xff && (body[1]! & 0xe0) === 0xe0);
    case "audio/aac":
      return body.length >= 2 && body[0] === 0xff && (body[1]! & 0xf6) === 0xf0;
    case "audio/wav":
    case "audio/x-wav":
      return body.length >= 12 && ascii(0, 4) === "RIFF" && ascii(8, 12) === "WAVE";
    case "audio/ogg":
      return ascii(0, 4) === "OggS";
    case "audio/flac":
      return ascii(0, 4) === "fLaC";
    case "audio/webm":
    case "video/webm":
      return starts(0x1a, 0x45, 0xdf, 0xa3);
    case "audio/mp4":
    case "audio/x-m4a":
    case "video/mp4":
    case "video/quicktime":
      return ftyp;
    default:
      return false;
  }
}

export function resourcePresentationKind(
  fileType: string | null,
): "pdf" | "image" | "audio" | "video" | "file" {
  const type = (fileType ?? "").toLowerCase();
  if (type === "application/pdf") return "pdf";
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("audio/")) return "audio";
  if (type.startsWith("video/")) return "video";
  return "file";
}

/** Only ordinary web links are accepted; javascript:, data:, file: etc. fail closed. */
export function safeExternalResourceUrl(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  return url.toString();
}
